import { useEffect, useState } from 'react';
import { Nadpis, CERVENA } from './ui';

/**
 * Přehled provozního dne a směny (Stopa objednávky, E6) — blok ve třetím
 * sloupci Objednávek u jednoho dne. Norma: třetí sloupec jsou čísla
 * a „K dořešení", ne editace — tady se jen čte.
 *
 * Kdo den otevřel a uzavřel, kdo za provoz postupně odpovídal, storna,
 * odebrané položky s důvody, zásahy adminů a doby přípravy a přijetí.
 */
export default function PanelProvoznihoDne({ den, obnova = 0, muted, isDark, kompaktni = false }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!den) { setData(null); return undefined; }
        let platne = true;
        fetch(`/api/objednavky/provozni-den?datum=${den}`, { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (platne) setData(d); })
            .catch(() => { if (platne) setData(null); });
        return () => { platne = false; };
    }, [den, obnova]);

    // Bez provozního dne i bez knihy (starší dny) není co ukázat.
    if (!data || (!data.den && !data.v_knize)) return null;

    const d = data.den;
    const radek = { display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '3px 0' };
    const hodnota = { color: 'var(--text-primary)', fontWeight: 600, textAlign: 'right' };
    const cara = isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-subtle)';

    return (
        <div style={kompaktni
            ? { padding: '12px 14px', borderRadius: 12, border: `1px solid ${cara}` }
            : { marginTop: 16, paddingTop: 14, borderTop: `1px solid ${cara}` }}>
            <Nadpis muted={muted}>Provozní den</Nadpis>
            {d && (
                <>
                    <div style={radek}>
                        <span style={{ color: muted }}>Otevřel</span>
                        <span style={hodnota}>{[d.otevrel, d.otevreno].filter(Boolean).join(' · ') || '—'}</span>
                    </div>
                    <div style={radek}>
                        <span style={{ color: muted }}>Uzavřel</span>
                        <span style={{ ...hodnota, color: d.zpusob === 'auto' ? CERVENA : hodnota.color }}>
                            {d.stav !== 'uzavreny' ? 'zatím otevřený'
                                : d.zpusob === 'auto' ? `automaticky ve 3:00` : [d.uzavrel, d.uzavreno].filter(Boolean).join(' · ')}
                        </span>
                    </div>
                    {d.zpusob === 'auto' && d.poznamka && (
                        <div style={{ fontSize: 11.5, color: muted, padding: '2px 0 4px' }}>{d.poznamka}</div>
                    )}
                </>
            )}
            {data.odpovidali?.length > 0 && (
                <div style={{ marginTop: 6 }}>
                    <span style={{ fontSize: 11.5, color: muted }}>Odpovídali</span>
                    {data.odpovidali.map((o, i) => (
                        <div key={i} style={radek}>
                            <span style={{ color: 'var(--text-primary)' }}>{o.jmeno || '—'}</span>
                            <span style={{ color: muted, fontVariantNumeric: 'tabular-nums' }}>{o.od}–{o.do}</span>
                        </div>
                    ))}
                </div>
            )}
            <div style={{ marginTop: 6 }}>
                {data.priprava_s != null && <Radek popis="Příprava položky (medián)" hodnota={doba(data.priprava_s)} styl={radek} hodnotaStyl={hodnota} muted={muted} />}
                {data.prijeti_s != null && <Radek popis="Přijetí objednávky (medián)" hodnota={doba(data.prijeti_s)} styl={radek} hodnotaStyl={hodnota} muted={muted} />}
                <Radek popis="Storna" hodnota={data.storna} styl={radek} hodnotaStyl={hodnota} muted={muted} />
                <Radek popis="Odebrané položky" hodnota={data.odebrane.pocet ? `${data.odebrane.pocet} · ${data.odebrane.kc} Kč` : 0} styl={radek} hodnotaStyl={hodnota} muted={muted} />
                {Object.entries(data.odebrane.duvody || {}).map(([duvod, n]) => (
                    <div key={duvod} style={{ fontSize: 11.5, color: muted, paddingLeft: 8 }}>{n}× {duvod}</div>
                ))}
                {data.zasahy?.map(z => (
                    <Radek key={z.jmeno} popis={`Zásahy: ${z.jmeno}`} hodnota={z.pocet} styl={radek} hodnotaStyl={hodnota} muted={muted} />
                ))}
            </div>
        </div>
    );
}

function Radek({ popis, hodnota, styl, hodnotaStyl, muted }) {
    return (
        <div style={styl}>
            <span style={{ color: muted }}>{popis}</span>
            <span style={hodnotaStyl}>{hodnota}</span>
        </div>
    );
}

function doba(s) {
    return s >= 60 ? `${Math.floor(s / 60)} min ${s % 60 ? `${s % 60} s` : ''}`.trim() : `${s} s`;
}
